import React, { useState } from 'react';
import { StarIcon, SparklesIcon, ShieldCheckIcon, PlayIcon, HeartIcon } from './Icons';
import { sounds } from '../utils/audio';

export default function LearnMathsSection({ onOpenPrivacy, onOpenContact }) {
  const [activeIsland, setActiveIsland] = useState(0);

  const islands = [
    {
      id: 'counting-cove',
      name: 'Counting Cove',
      color: 'bg-sky-50 border-sky-300 text-sky-900',
      tagColor: 'bg-sky-200 text-sky-800',
      icon: '✨',
      petName: 'Pip',
      petImg: '/assets/images/pet-pip.png',
      skills: 'Count to 20 • Bigger / Smaller • Number Line',
      desc: 'Count glowing fireflies on the moonlit cove, hop along the stepping stone number line, and solve wobbling mystery number boxes.',
      levels: '7 levels + Muddle Mini-Battle & Final Boss'
    },
    {
      id: 'shape-shore',
      name: 'Shape Shore',
      color: 'bg-emerald-50 border-emerald-300 text-emerald-900',
      tagColor: 'bg-emerald-200 text-emerald-800',
      icon: '📐',
      petName: 'Gloop',
      petImg: '/assets/images/pet-gloop.png',
      skills: '2D & 3D Shapes • Pattern Sequences • Corners & Sides',
      desc: 'Discover geometric shapes along the shore! Miss Kiki shows faint ghost outlines that illuminate green when the right shape is placed.',
      levels: '7 levels + Muddle Mini-Battle & Final Boss'
    },
    {
      id: 'symbol-bridge',
      name: 'Symbol Bridge',
      color: 'bg-amber-50 border-amber-300 text-amber-900',
      tagColor: 'bg-amber-200 text-amber-800',
      icon: '🪵',
      petName: 'Snuffle',
      petImg: '/assets/images/pet-snuffle.png',
      skills: 'Plank Addition • Apple Subtraction • Make 10',
      desc: 'Drop sturdy wooden planks into the bridge gap to cross safely. Cross out fallen apples and fill the 10-frame with smiling counters.',
      levels: '7 levels + Muddle Mini-Battle & Final Boss'
    },
    {
      id: 'tick-tock-tower',
      name: 'Tick-Tock Tower',
      color: 'bg-purple-50 border-purple-300 text-purple-900',
      tagColor: 'bg-purple-200 text-purple-800',
      icon: '⏰',
      petName: 'Chirp',
      petImg: '/assets/images/pet-chirp.png',
      skills: 'Analogue Clocks • Bell Bong Counting • Order of the Day',
      desc: 'Meet Little Tick (hour hand) and Tock (minute hand) to master o’clock and half-past, and count the ringing bell bongs at sunset.',
      levels: '7 levels + Muddle Mini-Battle & Final Boss'
    },
    {
      id: 'market-meadow',
      name: 'Market Meadow',
      color: 'bg-rose-50 border-rose-300 text-rose-900',
      tagColor: 'bg-rose-200 text-rose-800',
      icon: '🥭',
      petName: 'Penny',
      petImg: '/assets/images/pet-penny.png',
      skills: 'Counting Coins • Paying Prices • Sharing Equally • Doubles',
      desc: 'Select dollar coins to purchase fresh items, deal sweet mangoes equally between animal friends, and double delicious recipe ingredients.',
      levels: '7 levels + Muddle Mini-Battle & Final Boss'
    }
  ];

  return (
    <section id="maths" className="py-16 lg:py-24 bg-gradient-to-b from-white via-sky-50/40 to-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top Badges & App Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10 pb-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-3xl p-1 bg-gradient-to-tr from-sky-400 via-blue-500 to-indigo-600 shadow-lg flex-shrink-0">
              <img src="/assets/images/app-icon-math.png" alt="Learn Maths Icon" className="w-full h-full object-cover rounded-2xl" />
            </div>
            <div>
              <span className="px-3 py-1 rounded-full text-[11px] font-heading font-bold bg-kiki-blue text-white uppercase tracking-wider">
                iOS &amp; iPadOS App
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-black text-slate-900 mt-1">
                Kiki and Kids – Learn Maths
              </h2>
              <p className="text-xs sm:text-sm font-semibold text-slate-500">
                A Free, Offline Maths Adventure for Children Aged 4–8
              </p>
            </div>
          </div>

          {/* App download badge */}
          <div>
            <span className="px-4 py-2 rounded-2xl border-2 border-slate-200 text-xs font-heading font-bold text-slate-700 bg-white shadow-xs">
              Universal iOS App
            </span>
          </div>
        </div>

        {/* Hero Banner: Miss Kiki & Gameplay Features */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-600 rounded-[40px] text-white p-8 sm:p-12 shadow-2xl relative overflow-hidden mb-16">
          
          {/* Background sparkles */}
          <div className="absolute top-4 right-8 text-yellow-300 text-3xl opacity-60">✨</div>
          <div className="absolute bottom-6 left-1/3 text-sky-200 text-4xl opacity-40">⭐</div>

          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-xs font-heading font-bold text-white">
              <span>⛵ The Magical Archipelago</span>
            </div>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black leading-tight">
              Sail across 5 themed islands and save them from Muddle’s storm!
            </h3>
            <p className="text-slate-100 text-base sm:text-lg font-medium leading-relaxed max-w-xl">
              Miss Kiki guides your child with friendly spoken voice instructions. 
              No failure, no timers, and no pressure: every correct answer unlocks rewards, stickers, 
              and coins to furnish their personal treehouse.
            </p>

            {/* Feature Pills */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
              <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 text-center">
                <span className="font-heading font-bold text-lg block">35+ Levels</span>
                <span className="text-[11px] text-sky-100">Across 5 islands</span>
              </div>
              <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 text-center">
                <span className="font-heading font-bold text-lg block">0 Failure</span>
                <span className="text-[11px] text-sky-100">Gentle voice hints</span>
              </div>
              <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 text-center">
                <span className="font-heading font-bold text-lg block">100% Offline</span>
                <span className="text-[11px] text-sky-100">No ads or accounts</span>
              </div>
            </div>
          </div>

          {/* Right Visual: Miss Kiki Cheer + App Showcase */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative w-64 sm:w-72 aspect-[3/4] bg-white rounded-3xl p-4 shadow-2xl border-4 border-white/40 flex flex-col items-center justify-between text-slate-800">
              <div className="w-full flex items-center justify-between border-b pb-2 text-[11px] font-bold text-slate-400">
                <span>Miss Kiki’s Island Map</span>
                <span className="text-amber-500 font-bold">⭐⭐⭐</span>
              </div>
              
              <div className="w-44 h-44 my-auto flex items-center justify-center">
                <img src="/assets/images/kiki-cheer.png" alt="Miss Kiki Cheer" className="w-full h-full object-contain filter drop-shadow-md animate-bounce-slow" />
              </div>

              <div className="w-full p-2.5 rounded-xl bg-amber-50 border border-amber-200 text-center">
                <span className="font-heading font-bold text-xs text-amber-900 block">
                  "You did it! Level complete!"
                </span>
                <span className="text-[10px] text-amber-700">Star earned &amp; sticker unlocked</span>
              </div>
            </div>
          </div>

        </div>

        {/* The 5 Islands Interactive Explorer */}
        <div className="space-y-6">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <h3 className="text-3xl font-heading font-black text-slate-900">
              Explore the 5 Themed Math Islands
            </h3>
            <p className="text-slate-600 font-medium text-sm sm:text-base">
              Each island develops a core mathematical foundational concept through hands-on manipulatives.
            </p>
          </div>

          {/* Island Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {islands.map((island, index) => (
              <button
                key={island.id}
                onClick={() => { sounds.playSound('tap'); setActiveIsland(index); }}
                className={`px-4 py-2.5 rounded-2xl font-heading text-xs sm:text-sm font-bold border-2 transition-all flex items-center gap-2 ${
                  activeIsland === index
                    ? 'border-kiki-blue bg-kiki-blue text-white shadow-md scale-105'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                }`}
              >
                <span>{island.icon}</span>
                <span>{island.name}</span>
              </button>
            ))}
          </div>

          {/* Active Island Showcase Card */}
          <div className={`rounded-3xl border-3 p-6 sm:p-10 shadow-lg transition-all animate-pop ${islands[activeIsland].color}`}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              
              <div className="lg:col-span-8 space-y-4">
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-heading font-bold ${islands[activeIsland].tagColor}`}>
                    {islands[activeIsland].skills}
                  </span>
                  <span className="text-xs font-bold opacity-75">
                    {islands[activeIsland].levels}
                  </span>
                </div>

                <h4 className="text-2xl sm:text-3xl font-heading font-black">
                  {islands[activeIsland].name}
                </h4>

                <p className="text-sm sm:text-base font-medium leading-relaxed opacity-90">
                  {islands[activeIsland].desc}
                </p>

                <div className="pt-2 flex items-center gap-3">
                  <span className="text-xs font-bold uppercase tracking-wider opacity-75">Island Rescue Pet:</span>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white shadow-xs border text-xs font-bold text-slate-800">
                    <img src={islands[activeIsland].petImg} alt={islands[activeIsland].petName} className="w-6 h-6 object-contain" />
                    <span>Adopt {islands[activeIsland].petName}</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-4 flex justify-center">
                <div className="w-48 h-48 rounded-3xl bg-white p-4 shadow-md border flex flex-col items-center justify-center text-center">
                  <img src={islands[activeIsland].petImg} alt={islands[activeIsland].petName} className="w-28 h-28 object-contain animate-wiggle" />
                  <span className="font-heading font-bold text-xs text-slate-800 mt-2">
                    {islands[activeIsland].petName} the Pet
                  </span>
                  <span className="text-[10px] text-slate-500 font-semibold">Rescued after Boss Battle!</span>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Gamification Pillars */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 rounded-3xl bg-white border-2 border-slate-200/80 shadow-sm space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center text-2xl">
              ⚡
            </div>
            <h4 className="font-heading font-bold text-lg text-slate-900">Muddle Storm Battles</h4>
            <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed">
              Muddle the silly storm creature scrambles numbers! Children solve quick mini-equations to drain Muddle’s mess meter and bring back the island sunshine.
            </p>
          </div>

          <div className="p-6 rounded-3xl bg-white border-2 border-slate-200/80 shadow-sm space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center text-2xl">
              🏡
            </div>
            <h4 className="font-heading font-bold text-lg text-slate-900">Decorated Treehouse</h4>
            <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed">
              Every level completed awards coins. Children can purchase beds, telescopes, books, lamps, and teddy bears to furnish their cozy treehouse.
            </p>
          </div>

          <div className="p-6 rounded-3xl bg-white border-2 border-slate-200/80 shadow-sm space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center text-2xl">
              🛡️
            </div>
            <h4 className="font-heading font-bold text-lg text-slate-900">Safe Local Play</h4>
            <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed">
              Built with SwiftUI and local SwiftData storage. No internet required, no analytics SDKs, no accounts, and no accidental purchases.
            </p>
          </div>
        </div>

        {/* App Footer Info */}
        <div className="mt-12 p-6 rounded-3xl bg-slate-900 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ShieldCheckIcon className="w-6 h-6 text-kiki-green flex-shrink-0" />
            <span className="text-xs sm:text-sm font-semibold">
              Kiki and Kids – Learn Maths is available as a free download for iPad and iPhone.
            </span>
          </div>
          <div className="text-xs text-slate-400 font-medium">
            100% Offline • No Subscriptions • Safe for Kids
          </div>
        </div>

      </div>
    </section>
  );
}
